/**
 * Every number that both the client and the server must agree on lives here.
 * Changing one of these without redeploying both sides desyncs prediction.
 */

/** Simulation ticks per second. Inputs and physics run at this fixed rate. */
export const TICK_RATE = 64;
export const TICK_DT = 1 / TICK_RATE;

/** Server sends a world snapshot every N ticks (64 / 2 = 32 Hz). */
export const SNAPSHOT_INTERVAL_TICKS = 2;
export const SNAPSHOT_RATE = TICK_RATE / SNAPSHOT_INTERVAL_TICKS;

/**
 * How far in the past remote entities are rendered, so there is always a pair of
 * snapshots to interpolate between. Two snapshot intervals plus a little slack.
 */
export const INTERPOLATION_DELAY_MS = (1000 / SNAPSHOT_RATE) * 2.5;

/** Server keeps this many ticks of history for lag-compensated hit rewinding. */
export const LAG_COMPENSATION_HISTORY_TICKS = TICK_RATE; // 1 second
/** Hard cap on how far back a client may be rewound, to bound "shot around a corner". */
export const MAX_REWIND_MS = 250;

/** Client keeps this many unacknowledged inputs for reconciliation. */
export const INPUT_BUFFER_SIZE = 128;
/** Inputs are resent this many times to survive packet loss without retransmit logic. */
export const INPUT_REDUNDANCY = 3;

// ---------------------------------------------------------------------------
// Player capsule (approximated as an axis-aligned box for world collision)
// ---------------------------------------------------------------------------

export const PLAYER_RADIUS = 0.4;
export const PLAYER_HEIGHT = 1.8;
export const PLAYER_CROUCH_HEIGHT = 1.0;
export const PLAYER_EYE_OFFSET = 0.15; // below the top of the capsule
/** Must stay under 256: health is sent as a single byte in snapshots. */
export const PLAYER_MAX_HEALTH = 150;

/** Vertical distance the controller will step up without jumping. */
export const STEP_HEIGHT = 0.5;

// ---------------------------------------------------------------------------
// Movement — tuned for a fast, air-control-heavy arena shooter
// ---------------------------------------------------------------------------

export const GRAVITY = 26;
export const JUMP_SPEED = 8.2;

export const GROUND_MAX_SPEED = 8.0;
export const SPRINT_MAX_SPEED = 10.5;
export const CROUCH_MAX_SPEED = 3.6;
export const GROUND_ACCEL = 90;
export const GROUND_FRICTION = 8;

/** Quake-style air control: small max speed, high accel, enables strafe-jumping. */
export const AIR_MAX_SPEED = 1.4;
export const AIR_ACCEL = 70;

// Tuned so a sprint slide covers ~14 m over its 900 ms — a real gap-crosser —
// launching at slightly over 2× sprint and decaying back to sprint speed by
// the time it ends. The launch cap is what keeps slide-jump-reslide chains
// from compounding the impulse into unbounded speed.
export const SLIDE_IMPULSE = 11.0;
export const SLIDE_FRICTION = 0.8;
export const SLIDE_MIN_SPEED = 4.0;
export const SLIDE_MAX_LAUNCH = 21.5;
/** A slide survives being airborne this long (pad lips, small ledges). */
export const SLIDE_AIR_GRACE_MS = 150;
export const SLIDE_DURATION_MS = 900;
export const SLIDE_COOLDOWN_MS = 500;

/** Grace period after leaving a ledge during which jump still works. */
export const COYOTE_TIME_MS = 110;
/** Jump pressed this long before landing still fires on touchdown (bunny hopping). */
export const JUMP_BUFFER_MS = 130;

export const MAX_PITCH = Math.PI / 2 - 0.01;

// ---------------------------------------------------------------------------
// Combat
// ---------------------------------------------------------------------------

export const HEADSHOT_MULTIPLIER = 2.0;
/** Fraction of the capsule height, measured from the top, counted as a head. */
export const HEAD_FRACTION = 0.22;
export const MAX_SHOT_DISTANCE = 250;

// ---------------------------------------------------------------------------
// Match
// ---------------------------------------------------------------------------

/** Sized for the biggest vs-bot room: 4 humans + 4×4 bots. Wire counts are u8. */
export const MAX_PLAYERS_PER_MATCH = 20;

/**
 * Player ids at or above this are bots. Ids ride the wire as u16, so the range
 * itself tells the client who is a bot with no protocol change — used to skip
 * WebRTC voice peers and to mark the scoreboard. Socket ids count up from 1
 * and can never reach this.
 */
export const BOT_ID_START = 60_000;

export const isBotId = (id: number): boolean => id >= BOT_ID_START;

/**
 * The round clock — the fallback ender. Rounds are elimination: wiping a team
 * ends the round immediately, and only if both teams still stand at the buzzer
 * do the health sums decide it.
 */
export const ROUND_DURATION_MS = 90_000;
/** Round wins needed to take the whole game. */
export const ROUNDS_TO_WIN = 5;
/** The between-rounds banner: long enough to read who took the round. */
export const ROUND_END_DURATION_MS = 5_000;

/** Below this the match stays in `waiting` — nobody plays alone. */
export const MIN_PLAYERS_TO_START = 2;

/**
 * Hard ceiling on the loadout phase. A player who never picks does not hold the
 * lobby hostage: the countdown runs out and they drop in with the default weapon.
 */
export const LOADOUT_DURATION_MS = 30_000;
/**
 * Once everyone has locked in, the remaining countdown collapses to this so the
 * drop-in never feels abrupt and late readiness still saves real time.
 */
export const LOADOUT_READY_GRACE_MS = 3_000;
/**
 * The rematch window after a game is decided. If everyone presses Rematch the
 * same teams run it back; when the clock runs out (or anyone leaves early)
 * whoever is left returns to the lobby.
 */
export const MATCH_END_DURATION_MS = 30_000;

// ---------------------------------------------------------------------------
// Lobby pads
// ---------------------------------------------------------------------------

/** Countdown once a pad group is cleared to start. */
export const PAD_COUNTDOWN_MS = 3_000;
